<template>
    <div class="complete-verification-container" :style="containerStyle">
        <div class="step-label email-validation">Begin Identity Verification</div>
        <div class="step-label completed">Download Nametag App</div>
        <div class="step-label completed">Redirect to Nametag App</div>
        <div class="step-label active">Complete Identity Verification</div>

        <h1 class="header pb-0">
            Complete Identity Verification
        </h1>

        <transition name="fade">
            <div class="w-100">
                <div class="loader-container w-100" :style="{ display: !isVerificationInProgress ? 'none' : 'block' }">
                    <div class="loader-item">
                        <identity-validation-loading-overlay v-if="isVerificationInProgress" />
                        <p v-if="isVerificationInProgress" class="identity-validation text-mobile-center">Your identity
                            is being verified
                        </p>
                    </div>
                </div>

                <div v-if="!isVerificationInProgress && !identityValidationSuccess" class="handle-validation-failure">
                    <div class="identity-failure-container"><img class="error-image"
                            src="../assets/images/error_icon.png" alt="Error Image" />
                        <p class="identity-validation mb-0 text-mobile-center">Something went wrong and your identity
                            was not verified. Please try again.
                        </p>
                    </div>
                    <button @click="showManualVerification" class="next-button mt-3">{{ isRetryExceeded ?
        "Next" : "Begin verification again" }}</button>
                </div>
            </div>
        </transition>

        <transition name="slide-fade">
            <div v-if="identityValidationSuccess" class="password-mfa-reset-container w-100">
                <div class="loader-item verification-completed w-100 d-flex justify-content-center">
                    <div>
                        <font-awesome-icon v-if="identityValidationSuccess" class="checkmark" icon="check-circle" />
                    </div>
                    <p class="identity-validation completed mt-3">Your identity is now verified
                    </p>
                </div>

                <h2 class="action-header m-0 pl-0 pt-2 pb-3">What would you like to do next?</h2>
                <div class="radio-options">
                    <label>
                        <input class="radio-class" type="radio" value="Password Reset" v-model="selectedOption"
                            :disabled="last_password_reset" />
                        <h2 class="m-0 p-0 action-label">Create password</h2>
                    </label>
                    <label>
                        <input class="radio-class" type="radio" value="MFA Reset" v-model="selectedOption" :disabled="last_mfa_reset" />
                        <h2 class="m-0 p-0 action-label">Set up multi-factor authentication (MFA)</h2>
                    </label>
                </div>
            </div>
        </transition>

        <div class="next-steps ml-2 mt-2" v-if="selectedOption === 'Password Reset'">
            <p>When you click <b>Continue</b>, you’ll be redirected to Microsoft’s Online Password reset tool. Here
                are the
                steps to follow:</p>
            <ul>
                <li>Enter your Autodesk email address, {{ userEmail }}, complete the captcha, and click <b>Next</b>.
                </li>
                <li>Choose <b>Email my alternate email</b> and click <b>Get OTP</b>.</li>
                <li>Go to the alternate email account to get your One Time Password (OTP) , enter it in your browser,
                    and click <b>Next</b>.</li>
                <li>Create your new password, confirm it, and click <b>Finish</b>.</li>
            </ul>
        </div>

        <div class="next-steps ml-2 mt-2" v-if="selectedOption === 'MFA Reset'">
            <p>When you click <b>Continue</b>, you’ll be redirected to Microsoft’s Mysign-in page. Here are the steps to follow:</p>
            <ul>
                <li>On the next screen, sign in using your Autodesk email and new password.
                </li>
                <li>Follow the Set up your account prompts until you see a QR code on your screen.</li>
                <li>On the mobile device you’ll use for authentication, download Microsoft Authenticator app. Open the
                    app, select <b>Add Account</b>, then <b>Work</b> or <b>School Account</b>.</li>
                <li>If prompted, allow camera access and/or push notifications. They are both needed to set up your
                    account.</li>
                <li>Select Scan QR Code and use your mobile device to scan the QR code on your computer, then click
                    <b>Next</b>.
                </li>
                <li>Enter the number displayed in the webpage and click <b>Approve</b> on the pop-up window on your
                    mobile
                    device.</li>
                <li>Click Next on the webpage, then <b>Done</b>.
                </li>
            </ul>
        </div>

        <div class="loader-container w-100 mt-4" :style="{ display: mfaResetMySignInTrigger && !isVerificationInProgress ? 'block' : 'none' }">
            <div class="loader-item">
                <identity-validation-loading-overlay v-if="mfaResetMySignInTrigger && !isVerificationInProgress" />
                <p v-if="mfaResetMySignInTrigger && !isVerificationInProgress" class="identity-validation text-mobile-center ml-3">Please wait while your MFA setup begins. This phase takes 3-4 minutes.
                </p>
            </div>
        </div>

        <div class="d-flex justify-content-center w-100 mt-2" v-if="identityValidationSuccess">
            <button class="form-next-button mt-3 ml-3" @click="handleResetAction" :disabled="!selectedOption || mfaResetMySignInTrigger">
                Continue
            </button>
        </div>
        <loading-overlay v-if="isLoading"></loading-overlay>
    </div>
</template>

<script>
import LoadingOverlay from '@/components/LoadingOverlay.vue';
import IdentityValidationLoadingOverlay from '@/components/IdentityValidationLoadingOverlay.vue';
import { resetPasswordAndMfa } from '@/api/index.js';
import CONSTANTS from "@/constants";

export default {
    name: "CompleteIdentityVerification",
    components: {
        LoadingOverlay,
        IdentityValidationLoadingOverlay
    },
    props: {
        emailValidationStatus: {
            type: String,
            required: true
        },
        identityValidationStatus: {
            type: String,
            required: true
        },
        userEmail: {
            type: String,
            required: true
        },
        isRetryExceeded: {
            type: Boolean,
            required: true
        }
    },
    computed: {
        containerStyle() {
            return window.innerWidth <= 768 && this.isVerificationInProgress
                ? { marginTop: '-210px' }
                : {};
        }
    },
    data() {
        return {
            isLoading: false,
            isVerificationInProgress: false,
            identityValidationSuccess: null,
            selectedOption: "",
            warningMessages: CONSTANTS.WARNING_MESSAGES,
            mfaResetProcessingDelay: true,
            mfaResetMySignInTrigger: false,
            last_password_reset: false,
            last_mfa_reset: false
        };
    },
    mounted() {
        this.simulateIdentityValidation();
        this.requestPasswordMFAReset("Reset Date");
        setTimeout(() => {
            this.$router.go(0);
        }, 30 * 60 * 1000);
    },
    methods: {
        showManualVerification() {
            this.isRetryExceeded ? this.$emit("showManualVerification") : this.$router.push('/');
        },
        initiateResetAction(option) {
            const url = option === 'Password Reset' ? "https://aka.ms/sspr" : "https://mysignins.microsoft.com/security-info";
            window.open(url, "_blank");
            this.selectedOption = ""
        },
        proceedToHome() {
            var baseUrl = window.location.origin;
            window.location.href = baseUrl + '/';
        },
        isResetDoneWithin24hrs(reset_date) {
            if (reset_date == null) return false;
            const currentDateTime = new Date();
            const lastResetutcTime = new Date(reset_date).getTime();
            const lastResetDateIST = new Date(lastResetutcTime + (5.5 * 60 * 60 * 1000));
            const differenceInHours = (currentDateTime - lastResetDateIST) / (1000 * 60 * 60);
            return differenceInHours <= 24;
        },
        async requestPasswordMFAReset(action) {
            const response = await resetPasswordAndMfa(this.userEmail, action);
            const data = await response.json();
            if (this.selectedOption == "Password Reset") {
                this.initiateResetAction(this.selectedOption);
                this.last_password_reset = true
            } else if (this.selectedOption == "MFA Reset") {
                this.mfaResetMySignInTrigger = true;
                setTimeout(() => {
                    this.mfaResetProcessingDelay = false;
                    this.mfaResetMySignInTrigger = false;
                    this.initiateResetAction(this.selectedOption);
                    this.last_mfa_reset = true
                }, 4 * 60 * 1000);
            } else {
                this.last_password_reset = this.isResetDoneWithin24hrs(data.last_reset_date['Password Reset']);
                this.last_mfa_reset = this.isResetDoneWithin24hrs(data.last_reset_date['MFA Reset']);
            }
            this.isLoading = false;
        },
        simulateIdentityValidation() {
            this.isVerificationInProgress = true;
            setTimeout(() => {
                this.isVerificationInProgress = false;
                this.identityValidationSuccess = (this.emailValidationStatus === "Validated" && this.identityValidationStatus === "Verified");
                if (this.identityValidationSuccess) {
                    this.$emit("nextStep", 4);
                }
            }, 4000);
        },
        handleResetAction() {
            try {
                this.isLoading = true;
                this.requestPasswordMFAReset(this.selectedOption);
                this.isVerificationInProgress = false;
            } catch (error) {
                this.isLoading = false;
            }
        }
    }
};
</script>

<style scoped>
.complete-verification-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    height: auto;
    max-width: 1200px;
    color: black;
    font-family: artifaktElementRegular;
}

.handle-validation-failure {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    min-height: 150px;
    width: 100%;
}

.form-next-button:disabled {
    background-color: #ccc;
    color: #fff;
    cursor: not-allowed;
}

.identity-validation {
    margin-top: 0px !important;
}

.action-header {
    font-size: 16px;
    font-family: artifaktElementBold;
    margin-left: 10px;
}

.action-label {
    font-size: 14px;
}

.step-label.completed,
.step-label.active,
.email-validation {
    display: none;
}

.identity-failure-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 20px;
}

.error-image {
    width: 40px;
    margin-right: 20px;
}

.next-steps {
    width: 100%;
    border: 1px solid gray !important;
    padding: 10px;
    border-radius: 4px;
    font-size: 14px;
}

.next-button {
    background-color: #5f60ff;
    color: white;
    padding: 6px 40px;
    border: none;
    border-radius: 40px;
    font-weight: 700;
    font-size: 14px;
    cursor: pointer;
    margin-top: 40px;
    margin-bottom: 20px;
}

.password-info-list {
    list-style: none;
    list-style-position: inside;
    padding-left: 20px;
}

.password-info-list li {
    margin-bottom: 10px;
    text-align: start;
}

.password-info-list li::before {
    margin-right: 10px;
}

.password-info-list li::marker {
    color: #5a52d1;
    margin-right: 0px !important;
}

.next-button:hover:not(:disabled) {
    background-color: #5a52d1;
}

.header {
    font-size: 18px;
    font-weight: 700;
    color: black;
    font-family: artifaktElementBold;
    margin: 0px;
    margin-bottom: 20px;
    padding: 30px 10px;
}

.loader-container {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.checkmark.completed {
    font-size: 42px;
}

.loader-item {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
}

.loader-item p {
    margin: 0;
    margin-left: 10px;
    font-family: artifaktElementRegular;
}

.checkmark {
    color: green;
    font-size: 30px;
}

.crossmark {
    color: red;
    font-size: 24px;
}

.identity-validation {
    font-size: 16px;
    font-weight: 700;
}

.error-message {
    color: red;
    font-size: 16px;
    font-weight: 700;
    margin-top: 10px;
}

.disabled {
    pointer-events: none;
    opacity: 0.5;
}

.reset-password-header {
    font-size: 18px;
    font-weight: 700;
    color: black;
    font-family: artifaktElementBold;
    line-height: 34px;
    margin: 0px;
}

.password-mfa-reset-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-top: 20px;
    padding: 0px 10px;
}

.radio-class {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    position: relative;
    width: 16px !important;
    height: 16px;
    border-radius: 50%;
    outline: none;
    cursor: pointer;
}

.radio-class:checked {
    background-color: #5f60ff;
    border: none !important;
}

.radio-class::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    border-radius: 50%;
}

.modal {
    background: rgba(0, 0, 0, 0.5);
}

.modal-dialog {
    margin-top: 10%;
    margin-top: calc(15vh - 50px);
}

.radio-options {
    display: flex;
    flex-direction: column;
}

.radio-options label {
    margin-bottom: 10px;
}

.verification-completed {
    margin-right: 50px;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 1s;
}

.password-reset-container {
    font-family: artifaktElementRegular;
}

.modal-body p {
    color: black;
    font-size: 15px;
    text-align: start;
    font-family: artifaktElementRegular;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.slide-fade-enter-active {
    transition: all 0.5s ease;
}

.slide-fade-leave-active {
    transition: all 0.5s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
    transform: translateY(10px);
    opacity: 0;
}

.form-next-button {
    background-color: #5f60ff;
    color: white;
    padding: 6px 40px;
    border: none;
    border-radius: 40px;
    font-weight: 700;
    font-size: 14px;
    cursor: pointer;
}

.form-next-button:hover:not(:disabled) {
    background-color: #5a52d1;
}

.radio-options label {
    display: flex;
}

.radio-options input[type="radio"] {
    margin-right: 16px;
    border: 1px solid black;
}

.radio-options label p {
    font-weight: bold;
    margin-left: 10px;
}

@media (max-width : 700px) {
    .complete-verification-container {
        margin-top: -242px;
        border-left: 2px solid lightgray;
        padding: 0px 20px;
        margin-left: 14px;
    }

    .email-validation {
        margin-bottom: 30px;
    }

    .header {
        display: none;
    }

    .handle-validation-failure {
        min-height: 100px;
    }

    .action-label {
        width: 80%;
    }

    .loader-item {
        flex-direction: column;
    }

    .text-mobile-center {
        margin-top: 10px;
        text-align: center;
    }

    .identity-failure-container {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .error-image {
        width: 30px;
        margin: 0px;
    }

    .form-next-button {
        margin-left: 0px !important;
    }

    .step-label {
        font-weight: normal;
        font-size: 16px;
        margin-top: 3px;
        margin-bottom: 32px;
        display: block;
    }

    .step-label.completed {
        display: block;
        margin-bottom: 35px;
    }

    .step-label.active {
        display: block;
        font-weight: 900;
    }

    .identity-validation.completed {
        text-align: center;
    }

    .checkmark {
        color: green;
        font-size: 30px;
    }

    .password-mfa-reset-container {
        margin-top: 0px;
    }

}
</style>